import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function IlluTeam() {
    const svgRef = useRef(null);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;

        const ctx = gsap.context(() => {
            const header     = svg.querySelector('#tm-header');
            const userMsg    = svg.querySelector('#tm-user-msg');
            const supportMsg = svg.querySelector('#tm-support-msg');
            const footer     = svg.querySelector('#tm-footer');
            const badge      = svg.querySelector('#tm-badge');
            const stars      = svg.querySelectorAll('.tm-star');
            const onlineRing = svg.querySelector('#tm-online-ring');

            // ── initial states ──
            gsap.set(header,     { opacity: 0, y: -12 });
            gsap.set(userMsg,    { opacity: 0, x: 20 });
            gsap.set(supportMsg, { opacity: 0, x: -20 });
            gsap.set(footer,     { opacity: 0, y: 8 });

            // ── entry timeline ──
            const tl = gsap.timeline({
                scrollTrigger: { trigger: svg, start: 'top 85%', once: true },
                onComplete: startLoops,
            });

            tl
                .to(header,     { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' })
                .to(userMsg,    { opacity: 1, x: 0, duration: 0.42, ease: 'power2.out' }, 0.3)
                .to(supportMsg, { opacity: 1, x: 0, duration: 0.45, ease: 'power2.out' }, 0.85)
                .to(footer,     { opacity: 1, y: 0, duration: 0.4,  ease: 'power2.out' }, 1.15);

            function startLoops() {
                // Online dot pulse ring — r-based so it stays round
                gsap.fromTo(onlineRing,
                    { attr: { r: 7 }, opacity: 0.65 },
                    { attr: { r: 17 }, opacity: 0, duration: 1.3, ease: 'sine.out', repeat: -1, repeatDelay: 0.9 }
                );

                // Stars stagger-twinkle
                gsap.to(stars, {
                    opacity: 0.45, duration: 0.75, ease: 'sine.inOut',
                    yoyo: true, repeat: -1,
                    stagger: { each: 0.28 },
                });

                // Badge gentle scale pulse
                gsap.to(badge, {
                    scale: 1.06, svgOrigin: '362 269',
                    duration: 1.9, ease: 'sine.inOut', yoyo: true, repeat: -1,
                });
            }
        }, svgRef);

        return () => ctx.revert();
    }, []);

    return (
        <svg ref={svgRef} viewBox="0 0 460 290" fill="none" xmlns="http://www.w3.org/2000/svg" className="pxl-al-illu-svg">
            <defs>
                <linearGradient id="tm-bg" x1="0" y1="0" x2="1" y2="1">
                    <stop stopColor="#f0eaff"/><stop offset="1" stopColor="#e6deff"/>
                </linearGradient>
                <linearGradient id="tm-bubble-grad" x1="0" y1="0" x2="1" y2="1">
                    <stop stopColor="#7c3aed"/><stop offset="1" stopColor="#6d28d9"/>
                </linearGradient>
                <filter id="tm-shadow" x="-5%" y="-10%" width="110%" height="130%">
                    <feDropShadow dx="0" dy="6" stdDeviation="12" floodColor="#4c1d95" floodOpacity="0.09"/>
                </filter>
                <filter id="tm-msg" x="-8%" y="-12%" width="116%" height="140%">
                    <feDropShadow dx="0" dy="2" stdDeviation="5" floodColor="#000" floodOpacity="0.06"/>
                </filter>
                <clipPath id="tm-card-clip">
                    <rect x="18" y="14" width="424" height="272" rx="18"/>
                </clipPath>
            </defs>

            <rect width="460" height="290" fill="url(#tm-bg)"/>
            {/* Shadow outside clip so it isn't cut off */}
            <rect x="18" y="14" width="424" height="262" rx="18" fill="#fff" filter="url(#tm-shadow)"/>

            <g clipPath="url(#tm-card-clip)">

                {/* ── Header ── */}
                <g id="tm-header">
                    <circle cx="50" cy="44" r="22" fill="url(#tm-bubble-grad)"/>
                    <text x="50" y="51" textAnchor="middle" fontSize="17" fill="#fff" fontWeight="800" fontFamily="inherit">P</text>
                    {/* Static green dot */}
                    <circle cx="65" cy="59" r="7" fill="#22c55e" stroke="#fff" strokeWidth="2"/>
                    {/* Pulse ring — animated separately via r attribute */}
                    <circle id="tm-online-ring" cx="65" cy="59" r="7" fill="#22c55e" opacity="0"/>
                    <text x="86" y="38" fontSize="13" fontWeight="700" fill="#1a1535" fontFamily="inherit">PixaLab Support</text>
                    <text x="86" y="57" fontSize="10.5" fill="#22c55e" fontFamily="inherit">● Online · avg reply ~2h</text>
                    <line x1="28" y1="74" x2="432" y2="74" stroke="#f0ecfc" strokeWidth="1"/>
                </g>

                {/* ── User message (right-aligned) ── */}
                <g id="tm-user-msg">
                    <rect x="212" y="86" width="204" height="56" rx="16" fill="#ede9fe" filter="url(#tm-msg)"/>
                    <text x="228" y="108" fontSize="10.5" fill="#4b5563" fontFamily="inherit">Hi! When does my first</text>
                    <text x="228" y="126" fontSize="10.5" fill="#4b5563" fontFamily="inherit">payout arrive? 🤔</text>
                    <text x="414" y="154" textAnchor="end" fontSize="8.5" fill="#c4b5fd" fontFamily="inherit">2:12 PM  ✓✓</text>
                </g>

                {/* ── Support reply (left-aligned) ── */}
                <g id="tm-support-msg">
                    <circle cx="46" cy="182" r="15" fill="url(#tm-bubble-grad)"/>
                    <text x="46" y="187" textAnchor="middle" fontSize="10" fill="#fff" fontWeight="800" fontFamily="inherit">P</text>
                    <rect x="70" y="158" width="240" height="68" rx="16" fill="url(#tm-bubble-grad)" filter="url(#tm-msg)"/>
                    <text x="86" y="178" fontSize="10.5" fill="#fff" fontFamily="inherit">Payouts process on the 1st of</text>
                    <text x="86" y="194" fontSize="10.5" fill="#fff" fontFamily="inherit">each month via PayPal. You'll get</text>
                    <text x="86" y="210" fontSize="10.5" fill="#ddd6fe" fontFamily="inherit">an email when it ships! 💜</text>
                    <text x="70" y="238" fontSize="8.5" fill="#9ca3af" fontFamily="inherit">2:14 PM</text>
                </g>

                {/* ── Footer ── */}
                <g id="tm-footer">
                    <line x1="34" y1="250" x2="426" y2="250" stroke="#f0ecfc" strokeWidth="1"/>
                    {[0,1,2,3,4].map(i => (
                        <text key={i} className="tm-star" x={40 + i*22} y="269" fontSize="14" fill="#fbbf24" fontFamily="inherit">★</text>
                    ))}
                    <g id="tm-badge">
                        <rect x="288" y="257" width="148" height="24" rx="12" fill="#dcfce7"/>
                        <text x="362" y="273" textAnchor="middle" fontSize="9.5" fill="#15803d" fontWeight="600" fontFamily="inherit">⚡ Replied in 1h 42m</text>
                    </g>
                </g>

            </g>
        </svg>
    );
}
